/**
 * @fileoverview Reusable search input component
 * @description Renders an input field with optional left/right icons, commonly used for search bars
 * @active Client component [CONFIRMED]
 *
 * @see {@link src/components/ui/header/Header.tsx} - Example usage in header
 * @see {@link src/components/ui/header/SubHeader.tsx} - Example usage in subheader
 */

import React from "react"; // Import React for type definition
import { Flex, Input, InputProps, InputGroupProps } from "@chakra-ui/react";

import SearchSvg from "@/assets/svg/search.svg";
import { h_400_16_135 } from "@/styles/fontStyles";

/**
 * Props for the SearchInput component
 */
interface SearchInputProps {
  customStyleInputGroup?: InputGroupProps;
  customStyleInput?: InputProps;
  placeholder?: string;
  data: string;
  setData: React.Dispatch<React.SetStateAction<string>>;
  IconLeft?: React.ComponentType<any>;
  IconRight?: React.ComponentType<any>; // Optional component type
  NotIcon?: boolean; // Optional boolean
  // Update handlerKeyDown to accept the correct event type
  handlerKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClickIconLeft?: (e?: React.MouseEvent<SVGElement>) => void; // Allow optional event
  SendIcon?: React.ComponentType<any>; // Optional component type
}

/**
 * Renders a search input field with optional icons and event handlers
 *
 * @component SearchInput
 * @param {SearchInputProps} props - Component props
 * @returns {JSX.Element} Rendered search input
 */
export const SearchInput = ({
  customStyleInputGroup,
  customStyleInput,
  placeholder,
  data,
  setData,
  IconLeft,
  IconRight,
  NotIcon,
  handlerKeyDown = () => {},
  onClickIconLeft = () => {},
  SendIcon,
}: SearchInputProps): JSX.Element => {
  return (
    <Flex
      w={"100%"}
      alignItems={"center"}
      maxW={{ base: "100%", md: "660px" }}
      minH={"26px"}
      maxH={"50px"}
      border={"1.5px solid"}
      borderColor={"borderGray"}
      borderRadius={"10px"}
      columnGap={"8px"}
      px={"16px"}
      bgColor="dashboardBackground"
      {...customStyleInputGroup}
    >
      {NotIcon ? null : IconLeft ? (
        <IconLeft onClick={(e) => onClickIconLeft(e)} />
      ) : (
        <SearchSvg onClick={(e) => onClickIconLeft(e)} cursor={"pointer"} />
      )}
      <Input
        variant={"unstyled"}
        {...h_400_16_135}
        type="text"
        name={"search"}
        fontFamily={"Roboto"}
        placeholder={placeholder || "Search"}
        focusBorderColor="transparent"
        borderRadius={"none"}
        border={"none"}
        p={"0px"}
        _placeholder={{
          color: "gray",
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "1.3",
          letterSpacing: "2%",
        }}
        value={data}
        onChange={(e) => {
          setData(e.target.value);
        }}
        {...customStyleInput}
        onKeyDown={(e) => handlerKeyDown?.(e)} // Use optional chaining for handler
      />
      {IconRight && <IconRight />} {/* Render component if provided */}
      {SendIcon && <SendIcon />} {/* Render component if provided */}
    </Flex>
  );
};
